import React, { useRef, useState, useLayoutEffect, useCallback, useEffect } from 'react';
import { Globe } from './Globe';
import { trackVisitor } from '../../utils/visitorTracking';

import './Globe.scss';

// Expanded height = just enough to reveal the full globe (not a full square).
const getExpandedHeight = (el: HTMLElement): number => {
    const globe = el.querySelector<HTMLElement>('.globe-container');
    if (!globe) return el.offsetWidth;
    const bottomMargin = 64; // breathing room below the globe
    const globeBottom = globe.getBoundingClientRect().bottom - el.getBoundingClientRect().top;
    return Math.round(globeBottom + bottomMargin);
};

// Karan's fixed pin — same coords as the primary marker in Globe.tsx.
// Used for the "how far you are from me" distance readout.
const KARAN_LOC: [number, number] = [30.3165, 78.0322];

// Great-circle distance in km via the Haversine formula.
const distanceKm = (a: [number, number], b: [number, number]): number => {
    const R = 6371;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(b[0] - a[0]);
    const dLng = toRad(b[1] - a[1]);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
};

const formatDistance = (km: number): string => {
    const mi = km * 0.621371;
    const fmt = (n: number) => (n >= 100 ? Math.round(n).toLocaleString() : n.toFixed(1));
    return `${fmt(km)} km · ${fmt(mi)} mi`;
};

type LocStatus = 'idle' | 'prompting' | 'granted' | 'denied' | 'error' | 'unsupported';

export function GlobeDemo() {
    const containerRef = useRef<HTMLDivElement>(null);
    const collapsedHeight = useRef<number>(0);
    const pointerDown = useRef<{ x: number; y: number } | null>(null);
    const watchIdRef = useRef<number | null>(null);
    const [expanded, setExpanded] = useState(false);
    const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
    const [locStatus, setLocStatus] = useState<LocStatus>('idle');

    // Start watching the visitor's live position. Idempotent: repeated clicks
    // after a grant won't re-prompt; after a soft failure they can retry.
    const startWatching = useCallback(() => {
        if (watchIdRef.current !== null) return;
        if (!('geolocation' in navigator)) {
            setLocStatus('unsupported');
            return;
        }
        setLocStatus('prompting');
        const id = navigator.geolocation.watchPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                // Log the visitor to Discord + Google Sheets on the first
                // successful fix. trackVisitor() dedupes internally.
                trackVisitor([lat, lng], pos.coords.accuracy);
                // Suppress noisy sub-~50m updates. Each accepted change rebuilds
                // the WebGL globe (unavoidable — see cobe bug note in Globe.tsx),
                // so we only propagate when the visitor has actually moved.
                setUserLocation(prev => {
                    if (
                        prev &&
                        Math.abs(prev[0] - lat) < 0.0005 &&
                        Math.abs(prev[1] - lng) < 0.0005
                    ) {
                        return prev;
                    }
                    return [lat, lng];
                });
                setLocStatus('granted');
            },
            (err) => {
                // Surface the failure — silent denial is what makes the current
                // build feel "not asking for permission".
                console.warn('[GlobeDemo] geolocation error', err.code, err.message);
                if (err.code === err.PERMISSION_DENIED) {
                    setLocStatus('denied');
                } else {
                    setLocStatus('error');
                    // allow a retry on the next click for non-denial failures
                    if (watchIdRef.current !== null) {
                        navigator.geolocation.clearWatch(watchIdRef.current);
                        watchIdRef.current = null;
                    }
                }
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
        );
        watchIdRef.current = id;
    }, []);

    // Tear down the geolocation watch when the globe unmounts.
    useEffect(() => () => {
        if (watchIdRef.current !== null && 'geolocation' in navigator) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
    }, []);

    // Grow the card to a square (height = current width) on expand, or shrink
    // back to its natural height — both animated by the CSS height transition.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        if (expanded) {
            // start from an explicit height so the transition has a "from" value
            el.style.height = `${collapsedHeight.current}px`;
            const id = requestAnimationFrame(() => {
                el.style.height = `${getExpandedHeight(el)}px`;
            });
            return () => cancelAnimationFrame(id);
        }

        if (collapsedHeight.current) {
            el.style.height = `${collapsedHeight.current}px`;
        }
    }, [expanded]);

    const handlePointerDown = useCallback((e: React.PointerEvent) => {
        pointerDown.current = { x: e.clientX, y: e.clientY };
    }, []);

    const handlePointerUp = useCallback((e: React.PointerEvent) => {
        const start = pointerDown.current;
        pointerDown.current = null;
        if (!start) return;

        // Ignore drags (e.g. rotating the globe) — only a genuine click toggles.
        if (Math.abs(e.clientX - start.x) > 6 || Math.abs(e.clientY - start.y) > 6) return;

        const el = containerRef.current;
        if (el && !collapsedHeight.current) collapsedHeight.current = el.offsetHeight;

        // Expansion first, then the browser permission prompt — the tiny delay
        // lets the border-grow animation start before the modal steals focus.
        setExpanded(prev => !prev);
        window.setTimeout(startWatching, 180);
    }, [startWatching]);

    return (
        <div
            ref={containerRef}
            className={`globe-demo-container ${expanded ? 'expanded' : ''}`}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
        >
            <span className="globe-title">
                KARAN
            </span>
            <Globe className="top-28" userLocation={userLocation} />
            <div className="globe-overlay" />
            <div
                className={`globe-loc-status ${locStatus === 'granted' && userLocation ? 'granted' : locStatus}`}
                aria-live="polite"
            >
                {locStatus === 'idle' && '🌍 Click the globe to see how far you are from me'}
                {locStatus === 'prompting' && 'Requesting your location…'}
                {locStatus === 'granted' && userLocation && `📍 You're ${formatDistance(distanceKm(userLocation, KARAN_LOC))} from me`}
                {locStatus === 'denied' && 'Location blocked — enable it for this site in your browser to see how far you are from me.'}
                {locStatus === 'error' && 'Could not determine your location. Click the globe to try again.'}
                {locStatus === 'unsupported' && 'Your browser does not support geolocation.'}
            </div>
        </div>
    );
}
